import { useId, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { TOUCH_QUERY } from "../../../utils/useViewport";

interface TagsProps {
  availableTags: string[];
  checkedTags: ReadonlySet<string>;
  // Listed items per tag. A tag with none is disabled unless it is ticked,
  // so a ticked tag can always be unticked.
  tagCounts: Record<string, number>;
  onTagChange: (tag: string) => void;
}

// Touch screens: a list of checkboxes (tags here, categories in
// DirectoryLayout) becomes 40 px rows that toggle wherever they are tapped.
export const touchCheckboxRows = {
  [`@media ${TOUCH_QUERY}`]: {
    "& input": { width: 18, height: 18, alignSelf: "center" },
    "& label": {
      flex: 1,
      display: "flex",
      alignItems: "center",
      minHeight: 40,
    },
  },
};

const Tags = ({
  availableTags,
  checkedTags,
  tagCounts,
  onTagChange,
}: TagsProps) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const idPrefix = useId();

  const matchingTagIndices = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return availableTags.flatMap((tag, index) =>
      tag.toLowerCase().includes(query) ? [index] : [],
    );
  }, [searchQuery, availableTags]);

  const style = {
    "& label.Mui-focused": {
      color: "#9D2362",
    },
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderColor: "#9D2362",
      },
    },
  };

  return (
    <div>
      <TextField
        label="Search Tags"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={{ marginBottom: "10px", borderColor: "#9D2362" }}
        className="bg-white w-[100%]"
        color="secondary"
        sx={style}
      />
      <Box className="bg-white rounded-lg shadow-md p-4" sx={touchCheckboxRows}>
        {matchingTagIndices.map((index) => {
          const tag = availableTags[index];
          const count = tagCounts[tag];
          const id = `${idPrefix}-${index}`;
          return (
            <div key={index} className="grid grid-cols-[40px_1fr]">
              <div
                className={`mr-1 text-center col-start-1 col-span-1 flex items-center justify-center ${
                  !count ? "text-gray-400" : ""
                }`}
              >
                {count ? `(${count})` : "(0)"}
              </div>
              <div className=" col-start-2 col-span-1 flex justify-start">
                <input
                  type="checkbox"
                  id={id}
                  name={tag}
                  checked={checkedTags.has(tag)}
                  onChange={() => onTagChange(tag)}
                  disabled={!count && !checkedTags.has(tag)}
                />
                <label
                  htmlFor={id}
                  className={`ml-2 text-left ${!count ? "text-gray-400" : ""}`}
                >
                  {tag}
                </label>
              </div>
            </div>
          );
        })}
      </Box>
    </div>
  );
};

export default Tags;
